import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Responsible, ResponsibleApiResponse } from '../models/responsible.model';
import { environment } from '../../../environments/environment';
import { DocumentsService } from '../../core/services/documents.service';
import { FormDropdownService } from '../../core/services/form-dropdown.service';

@Injectable({
  providedIn: 'root'
})
export class ResponsibleRegistryService {
  private baseUrl = `${environment.backofficeApiUrl}/api`;

  constructor(
    private http: HttpClient,
    private documentsService: DocumentsService,
    private formDropdownService: FormDropdownService
  ) {
    this.formDropdownService.preloadSupplierTypes().subscribe({
      error: (err) => console.error('Error preloading company type options:', err),
    });
  }

  /**
   * Export responsabili in Excel
   */
  exportResponsiblesRegistry(responsibleId?: number | null): Observable<HttpResponse<Blob>> {
    return this.documentsService.exportRegistry('RESP_REF_01', null, responsibleId || null, null);
  }

  // =========================
  // GET - Get all responsibles
  // =========================
  getResponsibles(): Observable<ResponsibleApiResponse> {
    return this.http.get<ResponsibleApiResponse>(`${this.baseUrl}/v1/responsibles`);
  }

  // =========================
  // POST - Create responsible
  // =========================
  createResponsible(responsible: Responsible): Observable<ResponsibleApiResponse> {
    return this.http.post<ResponsibleApiResponse>(`${this.baseUrl}/v1/responsibles`, responsible, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  // =========================
  // GET - Get responsible by id
  // =========================
  getResponsible(id: string): Observable<ResponsibleApiResponse> {
    return this.http.get<ResponsibleApiResponse>(`${this.baseUrl}/v1/responsibles/${id}`);
  }

  // =========================
  // DELETE - Delete responsible
  // =========================
  deleteResponsible(id: string): Observable<ResponsibleApiResponse> {
    return this.http.delete<ResponsibleApiResponse>(`${this.baseUrl}/v1/responsibles/${id}`);
  }

  // =========================
  // PATCH - Partial update
  // =========================
  updateResponsiblePatch(id: string, responsibleData: Partial<Responsible>): Observable<ResponsibleApiResponse> {
    return this.http.patch<ResponsibleApiResponse>(`${this.baseUrl}/v1/responsibles/${id}`, responsibleData);
  }

  // =========================
  // PUT - Full update
  // =========================
  updateResponsiblePut(id: string, responsible: Responsible): Observable<ResponsibleApiResponse> {
    return this.http.put<ResponsibleApiResponse>(`${this.baseUrl}/v1/responsibles/${id}`, responsible);
  }

  // =========================
  // UTILITY - Map responsibles data for display
  // =========================
  private formatCompanyType(companyType?: string | null): string {
    if (!companyType) return '';

    return this.formDropdownService.decodeSupplierTypeSync(companyType);
  }

  formatResponsibleBusinessName(responsible: any): string {
    return responsible?.businessName || '';
  }

  /**
   * Mappa i dati dei responsabili per la visualizzazione nella tabella
   * Centralizza la logica per evitare duplicazioni nei componenti
   */
  mapResponsiblesForDisplay(responsibles: any[]): any[] {
    return responsibles.map((manager: any) => ({
      id: manager.responsibleId || '',
      businessName: manager.businessName || '',
      rawBusinessName: manager.businessName || '',
      companyType: manager.companyType || '',
      headquarters: manager.headquarters || '',
      email: manager.email || '',
      createdAt: manager.createdAt ? new Date(manager.createdAt).toLocaleDateString('it-IT') : '',
      status: manager.status || 'ACTIVE',
    }));
  }

  /**
   * Estrae l'array di responsabili dalla risposta API
   * Gestisce diversi formati di risposta (paginata, array, singolo oggetto)
   */
  extractResponsiblesArray(response: ResponsibleApiResponse): any[] {
    if (!response?.status || !response?.data) {
      return [];
    }

    let dataArray: any[] = [];
    
    // Check if data has content property (paginated response)
    if ('content' in response.data) {
      dataArray = Array.isArray(response.data.content) ? response.data.content : [response.data.content];
    } else if (Array.isArray(response.data)) {
      dataArray = response.data;
    } else {
      dataArray = [response.data];
    }

    return dataArray;
  }
}
